/**
 * `notify-embedder`-kortin viestin rakennus ja säännöt
 * (upotussivun-tapahtumaviesti-plan.md WP-UV2).
 *
 * <p>Viesti on litteä tapahtuma `{type, ...kentät}` (D3 = A): kiinteä `messageData`
 * ja lausekkeen tulos (D2 = B) yhdistetään niin, että lausekkeen avaimet voittavat
 * — sama kuvio kuin `submitExtraFields` lomakkeen arvojen päällä.
 *
 * <p><b>Tämä on turvaraja, ei palvelimen validointi.</b> Tallennusportti
 * (`AppContentValidator` NOTIFY_*) antaa vain aikaisen palautteen eikä näe
 * lausekkeen tulosta lainkaan. Rajat on pidettävä samoina molemmissa päissä —
 * ero tarkoittaisi korttia joka tallentuu mutta ei koskaan lähde.
 *
 * <p>Rike hylkää <b>koko viestin</b>: osittain lähetetty tapahtuma johtaisi
 * upottajan mittausta harhaan hiljaa, ja puuttuva tapahtuma näkyy ainakin
 * konsolissa.
 */

/** Tapahtuman tyyppi; `fasthub:`-etuliite on varattu sillan protokollalle (bridge.ts). */
const TYPE_PATTERN = /^[A-Za-z][A-Za-z0-9_.:-]{0,63}$/;
/** Kentän nimi. */
const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
/**
 * `type` ylikirjoittaisi tapahtuman tyypin; loput kolme ovat prototyyppiketjun
 * nimiä, joita ei haluta vieraan sivun käsiteltäväksi millään muodossa.
 */
const RESERVED_KEYS = new Set(['type', '__proto__', 'constructor', 'prototype']);
export const EMBEDDER_MAX_FIELDS = 20;
export const EMBEDDER_MAX_STRING = 200;

export type EmbedderValue = string | number | boolean | null;
export type EmbedderMessage = { type: string } & Record<string, EmbedderValue>;

export type BuildResult =
  | { ok: true; message: EmbedderMessage }
  | { ok: false; reason: string };

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

/**
 * Rakenna viesti kortin kentistä.
 *
 * @param type kortin `messageType`
 * @param literal kortin kiinteä `messageData` (puuttuva = ei kenttiä)
 * @param dynamic lausekkeen tulos kääreessä; `undefined` = korttiin ei ole
 *   kirjoitettu lauseketta. Kääre erottaa "ei lauseketta" tapauksesta "lauseke
 *   tuotti undefinedin" — jälkimmäinen on virhe (heittävä lauseke evaluoituu
 *   undefinediksi) eikä saa lähettää pelkkiä kiinteitä kenttiä kuin mitään ei
 *   olisi tapahtunut.
 */
export function buildEmbedderMessage(
  type: unknown,
  literal: unknown,
  dynamic?: { value: unknown },
): BuildResult {
  const t = typeof type === 'string' ? type.trim() : '';
  if (!t) return { ok: false, reason: 'messageType puuttuu' };
  if (t.toLowerCase().startsWith('fasthub:')) {
    return { ok: false, reason: `messageType '${t}' käyttää varattua fasthub:-etuliitettä` };
  }
  if (!TYPE_PATTERN.test(t)) return { ok: false, reason: `messageType '${t}' ei kelpaa` };

  if (literal != null && !isPlainObject(literal)) {
    return { ok: false, reason: 'messageData ei ole objekti' };
  }
  if (dynamic && !isPlainObject(dynamic.value)) {
    return { ok: false, reason: 'messageDataExpression ei tuottanut objektia' };
  }
  const merged: Record<string, unknown> = {
    ...(literal as Record<string, unknown> | undefined),
    ...(dynamic?.value as Record<string, unknown> | undefined),
  };

  const fields: Record<string, EmbedderValue> = {};
  let count = 0;
  for (const [key, value] of Object.entries(merged)) {
    // undefined = kenttää ei ole (esim. piilotettu lomakekenttä lausekkeessa),
    // kuten JSON.stringify sen tulkitsee — ei rike.
    if (value === undefined) continue;
    if (RESERVED_KEYS.has(key) || !KEY_PATTERN.test(key)) {
      return { ok: false, reason: `kentän nimi '${key}' ei kelpaa` };
    }
    if (typeof value === 'string') {
      if (value.length > EMBEDDER_MAX_STRING) {
        return { ok: false, reason: `kenttä '${key}' on yli ${EMBEDDER_MAX_STRING} merkkiä` };
      }
    } else if (typeof value === 'number') {
      if (!Number.isFinite(value)) return { ok: false, reason: `kenttä '${key}' ei ole äärellinen luku` };
    } else if (typeof value !== 'boolean' && value !== null) {
      return { ok: false, reason: `kenttä '${key}' ei ole merkkijono, luku, totuusarvo tai null` };
    }
    count += 1;
    if (count > EMBEDDER_MAX_FIELDS) {
      return { ok: false, reason: `yli ${EMBEDDER_MAX_FIELDS} kenttää` };
    }
    fields[key] = value as EmbedderValue;
  }
  // `type` ensin kuten asiakkaan esimerkissä; kentissä sitä ei voi olla (varattu).
  return { ok: true, message: { type: t, ...fields } };
}

/**
 * Normalisoi yksi origin vertailukelpoiseksi (v2, upotussivun-tapahtumaviesti-plan.md
 * §3.2) tai `null`, jos arvo ei ole origin.
 *
 * <p>Kelpaa vain `http(s)`-URL ilman polkua, queryä, fragmenttia ja käyttäjätietoa —
 * polullinen arvo ei saa hiljaa leventyä originiksi. Arvoksi otetaan `URL.origin`,
 * joka pudottaa oletusportin ja pienaakkostaa hostin. Ilman tätä palvelimen
 * portteineen tallentama `https://example.com:443` (frame-ancestors sallii sen)
 * ei koskaan täsmäisi selaimen ilmoittamaan `https://example.com`:iin, ja
 * upotus toimisi mutta viesti jäisi lähtemättä (v1:n piilevä vika).
 */
export function normalizeOrigin(candidate: string): string | null {
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.pathname !== '/' || url.search || url.hash || url.username || url.password) return null;
  return url.origin;
}

/**
 * Lue upotusoriginit shellin metan arvosta (välilyönnein eroteltu lista),
 * normalisoituina ja ilman kaksoiskappaleita. Kelvoton arvo ohitetaan: palvelin
 * validoi listan kirjoitettaessa (WP-EM1), mutta runtime ei luota siihen.
 */
export function parseEmbedOrigins(content: string | null | undefined): string[] {
  if (!content) return [];
  const out: string[] = [];
  for (const candidate of content.split(/\s+/)) {
    if (!candidate) continue;
    const origin = normalizeOrigin(candidate);
    if (origin && !out.includes(origin)) out.push(origin);
  }
  return out;
}

/**
 * Valitse `postMessage`-kohteet upottajalle (v2, §3.2).
 *
 * <p>Ensisijaisesti selaimen tiedossa oleva parentin origin
 * (`location.ancestorOrigins[0]`, Chromium ja WebKit): jos se on sallittu, viesti
 * lähtee vain sille; jos ei, ei minnekään. Vain kun selain ei kerro originia
 * (Firefoxista rajapinta puuttuu; peitetty origin on `'null'`), kohteena on koko
 * lista, ja selain toimittaa viestin täsmäävälle.
 *
 * <p>Toimitusjoukko on sama kuin v1:n "jokaiselle listan originille" -silmukassa,
 * koska selain vertaa kohdetta joka tapauksessa todelliseen parentiin. Ero on, että
 * Chromium kirjoittaa jokaisesta täsmäämättömästä kohteesta konsoliin varoituksen
 * (mitattu 2026-10-05) — upottajan sivulle, sanamuodolla joka näyttää siltä kuin
 * FastHub lähettäisi väärään osoitteeseen. Tämä välttää ne siellä, missä se on
 * mahdollista.
 */
export function embedderTargets(
  allowed: readonly string[],
  ancestorOrigins: ArrayLike<string> | null | undefined,
): string[] {
  const reported = ancestorOrigins && ancestorOrigins.length > 0 ? ancestorOrigins[0] : null;
  const parentOrigin = reported && reported !== 'null' ? normalizeOrigin(reported) : null;
  if (parentOrigin) return allowed.includes(parentOrigin) ? [parentOrigin] : [];
  return [...allowed];
}
